import useUser from "@app/zustand/user";
import { UserConnexionResponse } from "@api/src/types/responses";

export function useMostFreshUser(_calledFrom: string) {
  // console.log('useMostFreshUser called from', calledFrom);
  const cachedUser = useUser((state) => state.user);
  return cachedUser;
}

export async function refreshUser(_calledFrom: string) {
  if (!navigator.onLine) {
    // we need this because if offLine then the service worker return the latest GET /user/me
    // and it makes the prochain_bracelet_a_utiliser stale
    return null;
  }
  const controller = new AbortController();
  const { signal } = controller;
  // console.log("refreshUser", _calledFrom);

  try {
    const timeout = new Promise<null>((_, reject) =>
      setTimeout(() => {
        controller.abort(); // Abort the fetch when the timeout occurs
        reject(new Error("Timeout"));
      }, 5000),
    );

    // Get base URL with fallback (same logic as api.ts)
    // In production, always use relative URLs to avoid mixed content issues
    let baseUrl: string;
    if (import.meta.env.PROD) {
      baseUrl = "";
    } else {
      // In development, use VITE_API_URL or fallback to localhost
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) {
        baseUrl = "http://localhost:2222";
      } else if (apiUrl.startsWith("http://")) {
        // Force HTTPS in development if HTTP is provided
        console.warn(
          "VITE_API_URL uses HTTP. Converting to HTTPS to avoid mixed content errors.",
        );
        baseUrl = apiUrl.replace("http://", "https://");
      } else {
        baseUrl = apiUrl;
      }
    }

    // If using relative URLs, prepend /api for nginx proxying
    const apiPath = baseUrl === "" ? `/api/user/me` : `${baseUrl}/user/me`;
    const fetchPromise = fetch(apiPath, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
      signal, // Pass the signal to the fetch request
    }).then(async (response) => {
      // Good connection event only dispatched if no timeout
      window.dispatchEvent(new Event("good-connection"));
      if (response.status === 401) {
        useUser.getState().endSession();
        return null;
      }

      const userResponse = (await response.json()) as UserConnexionResponse;
      if (userResponse?.ok && userResponse.data?.user) {
        const user = userResponse.data.user;
        useUser.getState().setUser(user);
        return user;
      }

      return null;
    });

    return await Promise.race([timeout, fetchPromise]);
  } catch (error) {
    if (error instanceof Error && error.message === "Timeout") {
      // Timeout specific handling
      window.dispatchEvent(new Event("very-bad-connection"));
    } else if (error instanceof Error && error.name === "AbortError") {
      console.warn("Fetch aborted due to timeout.");
    } else {
      console.error("Failed to refresh user:", error);
    }
    return null;
  }
}
